import { UUID } from './data-types'

export enum AppCredentialsTypeEnum {
  APIKey = 'API Key',
  ClientIDSecret = 'Client ID and Client Secret',
  ClientIDSecretAndDomain = 'Client ID, Client Secret and Domain Name',
  EncodedAPIKeyAndWorkspaceID = 'Encoded API Key And WorkspaceID',
  EncodedUsernamePassword = 'Encoded Username and Password',
  OAuthCredentials = 'OAuth Credentials',
  UsernamePassword = 'Username and Password',
  UsernamePasswordAndAccessToken = 'Username, Password and Access Token',
  UsernamePasswordAndDatabase = 'Username, Password and Database Name'
}

export enum FailedStatusEnum {
  CodeFailedToCreate = 'CodeFailedToCreate',
  FailedToRetrieveAuthToken = 'FailedToRetrieveAuthToken',
  FailedToRetrieveSalesforceOrgID = 'FailedToRetrieveSalesforceOrgID',
  LoadObjectNotFound = 'LoadObjectNotFound',
  RemoteSiteSettingCreateFailed = 'RemoteSiteSettingCreateFailed'
}

export enum AppTypeEnum {
  ATTFleetManagement = 'ATTFleetManagement',
  AlvysTMS = 'AlvysTMS',
  ApolloELD = 'ApolloELD',
  Azuga = 'Azuga',
  BigRoad = 'BigRoad',
  BlueInkTech = 'BlueInkTech',
  CargoChief = 'CargoChief',
  CloneOps = 'CloneOps',
  ConvoyTracking = 'ConvoyTracking',
  Coretex = 'Coretex',
  ELD88 = 'ELD88',
  ERoad = 'ERoad',
  EldMandate = 'EldMandate',
  EldPeak = 'EldPeak',
  EmailRateConfirmation = 'EmailRateConfirmation',
  EvaELD = 'EvaELD',
  ExpediteAllBooking = 'ExpediteAllBooking',
  ExpediteAllTracking = 'ExpediteAllTracking',
  Ezlogz = 'Ezlogz',
  FMELD = 'FMELD',
  FleetComplete = 'FleetComplete',
  FleetLocate = 'FleetLocate',
  FourKitesCarrier = 'FourKitesCarrier',
  FourKitesCustomer = 'FourKitesCustomer',
  GPSInsight = 'GPSInsight',
  GeoTab = 'GeoTab',
  Greenscreens = 'Greenscreens',
  Highway = 'Highway',
  Hirschbach = 'Hirschbach',
  Hos247 = 'Hos247',
  Hutch = 'Hutch',
  ISAAC = 'ISAAC',
  Infios = 'Infios',
  JTS = 'JTS',
  Linxup = 'Linxup',
  McLeod = 'McLeod',
  McLeodAPIModule = 'McLeodAPIModule',
  Mercurygate = 'Mercurygate',
  MondoTracking = 'MondoTracking',
  Motive = 'Motive',
  MyCarrierPortal = 'MyCarrierPortal',
  MyLogs = 'MyLogs',
  OneNetwork = 'OneNetwork',
  OneStepGPS = 'OneStepGPS',
  Orbcomm = 'Orbcomm',
  Priority1 = 'Priority1',
  ProRide = 'ProRide',
  Project44 = 'Project44',
  /**
   * Used when a Chain customer needs to send tracking data to their customer's Project44 account. In that case, the Chain customer is a "Project44 Carrier"
   */
  Project44Carrier = 'Project44Carrier',
  QualityLogs = 'QualityLogs',
  'RMIS' = 'RMIS',
  RevenovaProduction = 'RevenovaProduction',
  RevenovaSandbox = 'RevenovaSandbox',
  SFTP = 'SFTP',
  SafeLane = 'SafeLane',
  Samsara = 'Samsara',
  Sonar = 'Sonar',
  Sunnybrook = 'Sunnybrook',
  SuperXELD = 'SuperXELD',
  SwiftELD = 'SwiftELD',
  TQLTrax = 'TQLTrax',
  Tai = 'Tai',
  Tailwind = 'Tailwind',
  ThreeGTMS = 'ThreeGTMS',
  ThreePLSystems = 'ThreePLSystems',
  ThreePLSystemsPortal = 'ThreePLSystemsPortal',
  Tive = 'Tive',
  Transporeon = 'Transporeon',
  TruckX = 'TruckX',
  TruckerToolsCarrier = 'TruckerToolsCarrier',
  TruckingHub = 'TruckingHub',
  Turvo = 'Turvo',
  Txt = 'TXT Solutions',
  US1NetworkTMS = 'US1NetworkTMS',
  VerizonConnectFleetTelogis = 'VerizonConnectFleetTelogis',
  VerizonConnectReveal = 'VerizonConnectReveal'
}

export enum AppCategoriesEnum {
  AICommunication = 'AI Communication',
  CarrierCapacity = 'Carrier Capacity',
  CarrierVetting = 'Carrier Vetting',
  DeviceTracker = 'Device Tracker',
  ELD = 'ELD',
  FreightRates = 'Freight rates',
  Integration = 'Integration',
  Rating = 'Rating',
  TMS = 'TMS',
  TemperatureMonitoring = 'Temperature Monitoring',
  ThirdPartyCarrierTrackingAPI = 'Third Party Carrier Tracking API',
  TrailerTracking = 'Trailer Tracking',
  VisibilityProvider = 'Visibility Provider'
}

export enum AppTriggerEnum {
  Cron = 'Cron',
  Event = 'Event'
}

export enum AssetTypeEnum {
  Trailer = 'Trailer',
  Truck = 'Truck'
}

export enum AssetResourceEnum {
  /**
   * Motorized assets are vehicles that are powered by an engine, such as trucks, cars, and motorcycles.
   */
  Motorized = 'Motorized',
  /**
   * Non-motorized assets are vehicles that are not powered by an engine, such as trailers, bicycles, trackable item and scooters.
   */
  NonMotorized = 'Non-Motorized'
}

export enum SalesforceSObjects {
  ApexClass = 'ApexClass',
  ApexTrigger = 'ApexTrigger',
  RemoteSiteSetting = 'RemoteSiteSetting'
}

export interface SavedSalesforceApexObjects {
  isApex: true
  objectID: string
  objectName: string
  type: SalesforceSObjects
}

export interface SavedSalesforceMetadataObjects {
  fullName: string
  isApex: false
  type: SalesforceSObjects
}

export interface AppCredentials {
  accessToken?: string

  /**
   * The api key provided by the third-party app
   */
  apiKey?: string
  /**
   * This is the api token that chaine provides to the app when possible
   */
  chaineAPIToken?: string
  /**
   * The client ID provided by the third-party app
   */
  clientID?: string

  /**
   * The client secret provided by the third-party app
   */
  clientName?: string

  /**
   * The client secret provided by the third-party app
   */
  clientSecret?: string

  /**
   * Base64 encoded client credentials in the format of username:password
   */
  encodedUsernamePassword?: string

  /**
   * The expiration time of the access token, in milliseconds, from the time it was issued by the third-party app.
   * This is updated every time the token is refreshed and is stored as a timestamp in milliseconds.
   */
  expiresIn?: number
  oAuthCredentials?: {
    /**
     * The access token provided by the third-party app. This is only saved the first time, and emitted in an event.
     */
    accessToken?: string
    /**
     * ISO 8601 date string of when the access token expires
     */

    accessTokenExpiresAt?: string
    /**
     * The expiration time of the access token, in milliseconds, from the time it was issued by the third-party app.
     * This is updated every time the token is refreshed and is stored as a timestamp in milliseconds.
     * @deprecated
     */
    expiresIn?: number
    /**
     * The refresh token provided by the third-party app.
     * This is only saved the first time, and emitted in an event.
     * When this eventually expires, the service that is using the app will refresh the token locally and save it in its own database.
     */
    refreshToken?: string
    /**
     * ISO 8601 date string of when the refresh token expires
     */
    refreshTokenExpiresAt?: string
    /**
     * The scope provided by the third-party app. This is only saved the first time, and emitted in an event.
     */
    scope?: string

    /**
     * The token type provided by the third-party app. This is only saved the first time, and emitted in an event.
     * This can be sometime like 'bearer' or 'Bearer'
     */
    tokenType?: string
  }
  password?: string
  sessionID?: string

  type?: AppCredentialsTypeEnum
  username?: string
}

export interface Project44Metadata {
  /**
   * This is true if the user has provided validated credentials. We use this to know what step on the web-client app to show the user when they are enabling a project44 app.
   */
  encodedCredentialsValidated?: boolean
  webhookDetails?: {
    /**
     * The name of the webhook, that project44 call's configName
     */
    configName: string
    /**
     * The registered webhook's ID
     */
    id: number
    /**
     * The webhook method (i.e. 'POST')
     */
    method: string
    /**
     * The encoded workspaceID, and API key that was given to project44 when making requests to Chaine
     * This will be in the query params
     */
    methodParams: {
      w: string
    }
    /**
     * The payload format of the webhook request i.e. JSON
     */
    payloadFormat: string
    /**
     * url of the webhook
     */
    url: string
  }
}

export type TOAuthMetadata = {
  accessToken?: string
  expiresIn?: number
  refreshToken?: string
  scope: string
  tokenType: string
}

export type TTiveMetadata = {
  /**
   * The access token provided by the third-party app, and it will be expired every 1 hour
   */
  accessToken: string
  /**
   * In tive there will be multiple accounts, and each account will have a client ID and client secret to get the access token for that account
   */
  accountID: string | undefined
  /**
   * The expiration time of the access token in milliseconds
   */
  expiresIn: number
  /**
   * The token type is Bearer for all responses
   */
  tokenType: string
}

export type SamsaraMetadata = TOAuthMetadata

export interface CarrierVettingAppsMetadata {
  onboardingURL: string
}

export interface DomainMetadata {
  domainName: string
}

export interface TQLTraxAppsMetadata {
  ocpApimSubscriptionKey: string
}

export interface HirschbachAppsMetadata {
  onboardingURL: string
}
export interface ISunnybrookAppsMetadata {
  baseURL: string
}

export interface IGeoTabMetadata {
  /**
   * The access token provided by the third-party app, and it will be expired after sometime unless they haven't provided the expiration time
   */
  accessToken?: string

  databaseName: string

  /**
   * The expiration time of the access token in milliseconds
   */
  expiresIn?: number

  /**
   * The server path returned by the Authenticate API call.
   * Can be "ThisServer" (use same domain as auth) or a different server domain.
   * This is used to route subsequent API calls to the correct GeoTab server.
   */
  serverPath?: string
}

export interface IFourKitesCustomerMetadata {
  customerID?: string
}

export interface IHos247Metadata {
  companyID: string
}

export interface ISafeLaneMetadata {
  accountID: string
  companyID: string
}

export interface ITruckXMetadata {
  carrierID: string
}

export interface ITurvoMetadata extends ITMSCommonMetadata {
  /**
   * Customer Country Code
   * field name from turvo is country
   */
  customerCountryCode: string

  /**
   * Customer Internal ID
   * field name from turvo is busId
   */
  customerInternalID: string

  /**
   * Name of the Customer who is using the Turvo app
   * field name from turvo is busName
   */
  customerName: string

  /**
   * default distance unit for the customer account
   * field name from turvo is distance
   */
  // languagetools:disable-next-line
  distanceUnit: 'mi' | 'km'

  /**
   * Email of the user who enabled the Turvo app
   * field name from turvo is email
   */
  emailOfUserWhoEnabledTurvoApp: string

  /**
   * Timezone offset difference (in minutes) from the Customer's timezone to the UTC timezone
   * field name from turvo is offset
   */
  offset: number

  /**
   * default temperature unit for the customer account
   * field name from turvo is temp
   */
  temperatureUnit: '°F' | '°C'

  /**
   * Default timezoneID for the customer
   * field name from turvo is timezone
   */
  timezoneID: string

  /**
   * User ID who enabled the Turvo app
   * field name from turvo is userId
   */
  userIDWhoEnabledTurvoApp: string

  /**
   * default weight unit for the customer account
   * field name from turvo is weight
   */
  weightUnit: 'lb' | 'kg'
}

export interface IMcLeodMetadata extends ITMSCommonMetadata {
  /**
   * The company ID for the McLeod instance. This is used in the "X-com.mcleodsoftware.CompanyID" header
   * Values are usually like TMS, TMS2, etc.
   */
  companyID: string
  /**
   * The last time the TMS load sync was requested
   */
  lastTMSLoadSyncDate?: string

  /**
   * The url for the McLeod instance specific to the user
   */
  mcLeodInstanceURL: string

  /**
   * McLeod's server timezone used to calculate offset for location and stop events
   */
  mcLeodServerTimezone: string

  /**
   * The version of the McLeod instance i.e. 21.2
   * McLeod version follows the nomenclature of year.release
   * Each year will always have a spring (1) and fall (2) release
   */
  mcLeodVersion: number

  pauseInboundProcessing?: boolean
}

export interface IMcLeodAPIModuleMetadata extends ITMSCommonMetadata {
  /**
   * The company ID for the McLeod instance. This is used in the "X-com.mcleodsoftware.CompanyID" header
   * Values are usually like TMS, TMS2, etc.
   */
  companyID: string

  /**
   * The last time the TMS load sync was requested
   */
  lastTMSLoadSyncDate?: string

  /**
   * The url for the McLeod instance specific to the user
   */
  mcLeodInstanceURL: string

  /**
   * McLeod's server timezone used to calculate offset for location and stop events
   */
  mcLeodServerTimezone: string
}

export interface IAlvysTMSMetadata extends ITMSCommonMetadata {
  /**
   * Alvys internal user ID extracted from the access token JWT (iid claim).
   * Tokens (accessToken, refreshToken) are stored in appCredentials.oAuthCredentials.
   */
  userIID: string
}

export interface InfiosMetadata {
  /**
   * The base URL of the Infios/MercuryGate API endpoint (received from the user).
   * Optional for backward compatibility with apps enabled before this field existed.
   */
  baseURL?: string

  /**
   * The receiver ID of the Infios app (received from the user)
   */
  receiverID: string
}

export interface RevenovaMetadata {
  /**
   * The external workspace ID for the Salesforce org
   */
  externalWorkspaceID: string
  // Although this can be any combo of scopes, right now we need "refresh_token full"
  /**
   * A signed data structure that contains authenticated user attributes, including a unique identifier for the user, and a timestamp indicating when the token was issued. It also identifies the requesting client app. See OpenID Connect specifications.
   * This parameter is returned if the scope parameter includes openid.
   */
  idToken?: string
  /**
   * An identity URL that can be used to identify the user, and to query for more information about the user.
   * See {@link https://help.salesforce.com/s/articleView?id=sf.remoteaccess_using_openid.htm&language=en_US&type=5 Identity URLs.}. For example: 'https://test.salesforce.com/id/00D050000008ppkEAA/00505000001k0FNAAY'
   */
  identityURL: string
  /**
   * A URL indicating the instance of the user's org. For example: https://johansontransportation--dev.sandbox.my.salesforce.com/.
   */
  instanceURL: string
  /**
   * Saves Salesforce objects. These are custom objects deployed in salesforce, and this information is needed if a user decides to disable the integration, which will delete it.
   */
  sObjects: (SavedSalesforceApexObjects | SavedSalesforceMetadataObjects)[]
  /**
   * The scopes associated with the access token.
   * Scopes further define the type of protected resources that the client can access. You assign scopes to a connected app when you build it, and they're included with the OAuth tokens during the authorization flow.
   * For valid parameters, see {@link https://help.salesforce.com/s/articleView?id=sf.remoteaccess_oauth_tokens_scopes.htm&type=5&language=en_US#oauth_scopes OAuth Scopes.?
   */
  scope: 'full' | 'full api' | 'api' | 'web openid' | 'refresh_token full'

  /**
   * Base64-encoded HMAC-SHA256 signature signed with the client_secret. The signature can include the concatenated ID, and issued_at value, which you can use to verify that the identity URL hasn't changed since the server sent it.
   */
  signature: string

  /**
   * A Bearer token type, which is used for all responses that include an access token.
   */
  tokenType: 'Bearer'
}

export type SFTPMetadata = {
  /**
   * This is the same as the username for the SFTP server. This is added for compatibility with other app metadata where we save the app in various systems, and add a gsi_pk_1 using the externalWorkspaceID.
   */
  externalWorkspaceID: string

  /**
   * The username to use for sending, and receiving files
   */
  username: string

  /**
   * The workspace ID
   */
  workspaceID: string
}

export interface IThreePLSystemsMetadata {
  baseURL: string
  /**
   * The client ID from the 3PL Systems id_token (decoded from JWT)
   */
  clientID?: string
  /**
   * The IANA timezone of the 3PL Systems TMS instance (e.g., 'America/New_York').
   * Used to correctly interpret times from 3PL which are sent as UTC but actually
   * represent wall-clock times in the TMS's configured timezone.
   */
  tmsTimezone: string
}

export type EmailRateConfirmationMetadata = {
  /**
   * The email addresses to send the rate confirmation email to
   */
  allowedEmailAddresses: string[]

  /**
   * The unique ID for the workspace to use in external email rate confirmation
   */
  externalWorkspaceID: string

  /**
   * The email address to send the rate confirmation email from
   */
  rateConfirmationEmail: string

  /**
   * The workspace ID
   */
  workspaceID: string
}

export type AppMetadata =
  | IAlvysTMSMetadata
  | IISAACMetadata
  | CarrierVettingAppsMetadata
  | DomainMetadata
  | EmailRateConfirmationMetadata
  | HirschbachAppsMetadata
  | IFreightVisibilityAppMetadata
  | ICloneOpsMetadata
  | IGeoTabMetadata
  | IMcLeodMetadata
  | Project44Metadata
  | RevenovaMetadata
  | SamsaraMetadata
  | SFTPMetadata
  | InfiosMetadata
  | TOAuthMetadata
  | ITMSCommonMetadata
  | TQLTraxAppsMetadata
  | TTiveMetadata
  | ITurvoMetadata
  | ITransporeonMetadata
  | IFourKitesCustomerMetadata
  | ISunnybrookAppsMetadata
  | IExpediteAllTrackingMetadata
  | IOneNetworkMetadata
  | IHos247Metadata
  | ISafeLaneMetadata
  | ITruckXMetadata
  | IProject44CarrierMetadata
  | IThreeGTMSMetadata
  | IThreePLSystemsMetadata
  | ITruckerToolsCarrierMetadata
export interface IThreeGTMSMetadata {
  /**
   * The SFTP server host
   */
  host?: string

  /**
   * The SFTP server port number
   */
  port?: number

  /**
   * The 3GTMS organization to include in the XML rate request.
   */
  rateApiOrganization?: string

  /**
   * The 3GTMS password for rate API requests.
   */
  rateApiPassword?: string

  /**
   * The 3GTMS trading partner/client identifier to include in the XML rate request.
   */
  rateApiTradingPartnerNum?: string

  /**
   * The synchronous 3GTMS rate API endpoint for contract lookups.
   */
  rateApiUrl?: string

  /**
   * The 3GTMS username for rate API requests.
   */
  rateApiUsername?: string

  /**
   * When true, contract rates from the 3GTMS rate API are synced to Aurora
   * lane contract rows for eligible NeedsCarrier shipments.
   */
  syncContractRates?: boolean
}
export interface IISAACMetadata {
  /**
   * The portal server number from the ISAAC portal URL
   * (e.g., "405" from https://ct405.isaachosting.ca)
   */
  portalServerNumber?: string

  /**
   * The server path/domain for API calls. For ISAAC, this is constructed
   * from the portalServerNumber (e.g., "ct405.isaachosting.ca")
   */
  serverPath?: string
}

export interface IOneNetworkMetadata {
  /**
   * The portal server number from the ISAAC portal URL
   * (e.g., "405" from https://ct405.isaachosting.ca)
   */
  orgName?: string

  /**
   * The server path/domain for API calls. For ISAAC, this is constructed
   * from the portalServerNumber (e.g., "ct405.isaachosting.ca")
   */
  serverPath?: string
}

export interface IExpediteAllTrackingMetadata {
  /**
   * The customer ID from the Expedite All app
   */
  customerID?: string
}

export interface IAppEntity {
  /**
   * The category of the app whether it's related to ELD or TMS or any freight rates app {@link AppCategoriesEnum}
   */
  appCategory?: AppCategoriesEnum

  /**
   * App specific credentials to make requests, and any credentials chaine provided to the app for making requests of type {@link IAppCredentials}
   */
  appCredentials: AppCredentials

  /**
   * Each app's additional metadata
   */

  appMetadata: AppMetadata

  /**
   * Type of the app to enable {@link appType AppsType}
   */
  appType: AppTypeEnum

  /**
   * Whether an app is disabled (deleted)
   */
  deleted: boolean

  /**
   * Whether the app is enabled
   */
  enabled: boolean

  /**
   * The user that enabled the app
   */
  enabledByUserID?: string

  /**
   * The enterprise workspace ID of the workspace that enabled the app
   */
  enterpriseWorkspaceID?: string

  /**
   * The unique ID for the workspace to use in external email rate confirmation
   */
  externalWorkspaceID?: string
  /**
   * The time enabling failed
   */
  failedToEnableAt?: number

  /**
   * The status message of enabling failed
   */
  failedToEnableStatus?: FailedStatusEnum

  /**
   * The description of the failed error message
   */
  failedToEnableStatusMessage?: string

  /**
   * The unique ID for the app
   */
  id?: UUID

  /**
   * If this app creates any integration configurations as a result of enabling, this will be the list of IDs of those configurations
   */
  integrationConfigurations?: { eventType: string; id: string }[]

  /**
   * Whether the app has an autopilot action. When this is true, this app will appear as an Action in the Autopilot rules
   */
  isAutopilotAction?: boolean

  /**
   * Whether the app is cron based or event based {@link AppTriggerEnum}
   */
  isCronBasedOrEventBased?: AppTriggerEnum

  /**
   * The workspaceID of the workspace that enabled the app
   */
  workspaceID: string
}

/**
 * A metadata interface to be used for apps that want to accept tracking data for visibility from Chaine workspaces
 */
export interface IFreightVisibilityAppMetadata {
  customers: {
    /**
     * This is the customer ID in the third-party app
     */
    customerID: string
    /**
     * This is the id of the customer in Chaine. This is used for filtering events in esg-to-external
     */
    customerIDInChaine: string
    /**
     * This is the name of the customer in Chaine. This is used for filtering events in esg-to-external
     */
    customerNameInChaine: string
    /**
     * This is the integration configuration ID created when app is enabled by broker, this id will help to track the old configurations added by broker before he added or deleted customers. So it is helpful to track on UI which configurations we have to delete based on the integration configuration IDs.
     */
    integrationConfigurationID?: string
  }[]
}

/**
 * Override values for outbound Trucker Tools location payload fields.
 * Matching still uses the default carrier MC number.
 */
export interface ITruckerToolsCarrierOverride {
  /**
   * The broker MC number to send to Trucker Tools when override is enabled
   */
  brokerMC: string
  /**
   * The carrier DOT number to send to Trucker Tools when override is enabled
   */
  dotNumber: string
  /**
   * The carrier MC number to send to Trucker Tools when override is enabled
   */
  mcNumber: string
  /**
   * The carrier SCAC code to send to Trucker Tools when override is enabled
   */
  scacCode: string
}

/**
 * Carrier configuration for Trucker Tools Carrier visibility provider
 */
export interface ITruckerToolsCarrierCarrier {
  /**
   * The broker MC number used for outbound payloads to Trucker Tools
   */
  brokerMC: string
  /**
   * The carrier DOT number used for outbound payloads to Trucker Tools
   */
  dotNumber: string
  /**
   * The integration configuration ID created when app is enabled. Used to track configs when adding or removing carriers.
   */
  integrationConfigurationID?: string
  /**
   * The MC (Motor Carrier) number of the carrier
   */
  mcNumber: string
  /**
   * The provider identifier shared with Trucker Tools for load tracking. Provided by the Trucker Tools team.
   */
  provider: string
  /**
   * The SCAC (Standard Carrier Alpha Code) of the carrier
   */
  scacCode: string
  /**
   * Optional outbound override details sent to Trucker Tools when enabled.
   */
  truckerToolsOverride?: ITruckerToolsCarrierOverride
}

/**
 * Metadata interface for Trucker Tools Carrier visibility provider app
 */
export interface ITruckerToolsCarrierMetadata {
  carriers: ITruckerToolsCarrierCarrier[]
}

export interface IProject44CarrierMetadata {
  customers: {
    /**
     * This is the shipment identifier type in the third-party app
     */
    ShipmentIdentifierType: string
    /**
     * This is the customer ID in the third-party app
     */
    p44CustomerID: string
    /**
     * This is the TMS customer ID in the third-party app
     */
    tmsCustomerID: string
  }[]
}

/**
 * A metadata interface to be used for CloneOps app
 */
export interface ICloneOpsMetadata {
  /**
   * The clone ops account ID provided by the third-party app
   */
  cloneOpsAccountID: string
}

/**
 * A metadata interface to be used for Transporeon app
 */
export interface ITransporeonMetadata {
  customers: {
    /**
     * This is the name of the customer in Chaine. This is used for filtering events in esg-to-external
     */
    customerNameInChaine: string

    /**
     * This is the customer sequence number created when app is enabled by broker, this number will help to track the old configurations added by broker before he added or deleted customers
     */
    customerSequenceNumber: number

    /**
     * This is the integration configuration ID created when app is enabled by broker, this id will help to track the old configurations added by broker before he added or deleted customers. So it is helpful to track on UI which configurations we have to delete based on the integration configuration IDs.
     */
    integrationConfigurationID?: string
  }[]
}

export interface IEventType {
  eventType: string
  id: UUID
}

export interface ITMSCommonMetadata {
  /**
   * Email domains
   */
  emailDomains?: string[]

  /**
   * The url for the TMS instance specific to the user
   */
  tmsURL?: string
}
